import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  RestaurantDeleteResp,
  RestaurantDeleteVariables,
  Restaurant
} from "../../types/restaurantTypes";

const DELETE_RESTAURANT = gql`
  mutation DeleteRestaurant($attributes: DeleteRestaurantInput!) {
    deleteRestaurant(attributes: $attributes) {
      restaurant {
        id
        name
      }
      errors
    }
  }
`   

type UseDeleteRestaurantOptions = {
  onCompleted?: (Restaurant: Restaurant) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<RestaurantDeleteResp, RestaurantDeleteVariables>>[1];
};

const useDeleteRestaurant = (options?: UseDeleteRestaurantOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    RestaurantDeleteResp,
    RestaurantDeleteVariables
  >(DELETE_RESTAURANT, options?.mutationOptions);

  const deleteRestaurant = useCallback(
    async (input: RestaurantDeleteVariables['attributes']) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!
            },
          },
        });

        const deleteRestaurant = result?.data?.deleteRestaurant;

        if (deleteRestaurant?.errors?.length) {
          throw new Error(deleteRestaurant.errors.join(", "));
        }

        if (options?.onCompleted && deleteRestaurant?.restaurant) {
          options.onCompleted(deleteRestaurant.restaurant);
        }

        return deleteRestaurant?.restaurant;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const restaurant = data?.deleteRestaurant?.restaurant;

  return {
    deleteRestaurant,
    deletedRestaurant: restaurant,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useDeleteRestaurant
